// ============================================================
// MongoDBBackup.jsx
//
// A branded guide to MongoDB backup strategies, data exports,
// and disaster recovery procedures.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #F9D71C  (Amber / Caution & Recovery)
//   Accent      → #00ED64  (MongoDB Green)
//   Text        → #FFFFFF  (White)
//   Terminal    → #010409  (High-Contrast Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Alert,
    Button
} from "@mui/material";

/* MUI Icons */
import BackupIcon from "@mui/icons-material/Backup";
import RestoreIcon from "@mui/icons-material/Restore";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import TerminalIcon from "@mui/icons-material/Terminal";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SecurityIcon from "@mui/icons-material/Security";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(249, 215, 28, 0.2)", // Amber Border
    amber: "#F9D71C",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CommandTerminal({ code, label, color = BRAND.amber }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#FDE68A", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBBackup() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.amber }} />}
                        label="Database Admin · Module 17"
                        sx={{ bgcolor: "rgba(249,215,28,0.1)", color: BRAND.amber, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Backup & <span style={{ color: BRAND.amber }}>Recovery</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Protect your data against hardware failure, human error, or malicious attacks.
                        Master the tools to snapshot your BSON data and restore it in seconds.
                    </Typography>
                </Box>

                {/* 2. BACKUP VS EXPORT */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <ImportExportIcon size={32} color={BRAND.amber} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Choosing the Right Tool</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <Typography variant="h6" sx={{ color: BRAND.amber, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                    <BackupIcon /> Binary Backups (BSON)
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
                                    Use <strong>mongodump</strong> for full database snapshots. It captures data and indexes in
                                    binary format, ensuring 100% fidelity upon restoration.
                                </Typography>
                                <CommandTerminal label="Terminal: Backup All" code="mongodump --uri='mongodb://localhost:27017' --out=/backups/" />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 4, bgcolor: BRAND.surface, border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, height: '100%' }}>
                                <Typography variant="h6" sx={{ color: BRAND.white, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                    <ImportExportIcon /> Logical Exports (JSON/CSV)
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
                                    Use <strong>mongoexport</strong> to move data into Excel, reporting tools, or other databases.
                                    Note: Exports do <strong>not</strong> preserve indexes or all BSON types perfectly.
                                </Typography>
                                <CommandTerminal label="Terminal: Export Collection" code="mongoexport --db=shop --collection=users --out=users.json" />
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. RESTORING DATA */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
                        <CommandTerminal
                            label="Terminal: Full Restore"
                            code="mongorestore --uri='mongodb://localhost:27017' /backups/"
                        />
                        <CommandTerminal
                            label="Terminal: JSON Import"
                            color={BRAND.green}
                            code="mongoimport --db=test --collection=users --file=users.json"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <RestoreIcon sx={{ color: BRAND.amber, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>The Recovery Process</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            Restoring is the inverse of backing up. <code>mongorestore</code> reads your dump files
                            and recreates the collections and indexes exactly as they were.
                        </Typography>
                        <Alert severity="error" sx={{ bgcolor: "rgba(255,82,82,0.05)", color: BRAND.white, border: "1px solid #FF5252" }}>
                            <Typography variant="body2">
                                <strong>Warning:</strong> Restoring data can overwrite existing documents. Always test your
                                restore process in a <strong>Playground</strong> environment first!
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>

                {/* 4. CLOUD BACKUPS (ATLAS) */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 237, 100, 0.03)", border: "1px solid rgba(0, 237, 100, 0.2)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <CloudSyncIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Automated Cloud Backups</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        If you are using <strong>MongoDB Atlas</strong>, manual dumps are rarely needed. Atlas provides
                        Continuous Cloud Backups with Point-in-Time Recovery (PITR).
                    </Typography>



                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <SecurityIcon sx={{ color: BRAND.green, fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Encrypted Snapshots</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <SecurityIcon sx={{ color: BRAND.green, fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>Multi-Region Redundancy</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <SecurityIcon sx={{ color: BRAND.green, fontSize: 18 }} />
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>1-Click Restore</Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #6B5900)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <TerminalIcon style={{ fontSize: 40, color: BRAND.amber, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Backup Policy Verified?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Never go live without a verified backup strategy. Practice a restore now to ensure your data's safety.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.amber, color: BRAND.bg, fontWeight: 900 }}>Run Test Dump</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>View DR Plan</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
